import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Tooltip, useMediaQuery } from '@mui/material'
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import gaiaTheme from '../../../../../root-components/theme';
import { PageOuterContainer } from '../../../../../root-components/layouts/page-outer-container';
import { SettingsMobileNavbar } from '../../../../../root-components/navigation/settings-mobile-navbar';
import { PageHeading } from '../../../../presentation-components/display-elements-components/page-heading';
import { TableDisplay } from '../../../../presentation-components/miscellaneous-components/table-display';
import { IHeadCell } from '../../../../../shared/interfaces/table-display.model';
import FileUploadService from '../../../../../shared/services/file-upload/file-upload.service';
import { IFileUpload } from '../../../../../shared/services/file-upload/file-upload.model';
import { ReportPeriodContext } from '../../../../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications';
import { LoadingPage } from '../../../loading-page';
import moment from 'moment';
import { ScreenLabelsContext } from '../../../../../context/screen-labels/screen-labels';
import { keyframes } from '@mui/material';

const flashAnimation = keyframes`
  0% {
    opacity: 0.1;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.1;
  }
`;

export const DataImportHistoryPage = () => {
  const navigate = useNavigate();
  //Services
  const fileUploadService = new FileUploadService();
  //Context
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userNotificationsContext = useContext(UserNotificationsContext);
  //State
  const [selectedImportType, setSelectedImportType] = useState<string>('all')
  const [allFileUploads, setAllFileUploads] = useState<IFileUpload[]>([]);
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [selected, setSelected] = useState<string[]>([]);
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>('uploadDate')
  const [order, setOrder] = useState<'asc' | 'desc'>('desc')
  //Loading State
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Use Effects
  useEffect(() => {
    getAllFileUploads();
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (inputPage === 0) {
      setChange(change + 1)
    } else {
      setInputPage(0)
    }
  }, [order, orderBy, selectedImportType, reportPeriodContext.lastSelectedReportId])

  // Poll for updates on IN_PROGRESS or PENDING uploads every 5 seconds
  useEffect(() => {
    const updateInProgressUploads = async () => {
      // Find uploads that are IN_PROGRESS or PENDING (either parsing/uploading or importing)
      const uploadsToUpdate = allFileUploads.filter(
        upload => upload.lineImportStatus === 'IN_PROGRESS' || upload.lineImportStatus === 'PENDING'
          || upload.lineCommitStatus === 'IN_PROGRESS' || upload.lineCommitStatus === 'PENDING'
      );
      if (uploadsToUpdate.length === 0) {
        return; // No uploads to update
      }
      try {
        // Fetch updated data for each upload
        const updatePromises = uploadsToUpdate.map(upload =>
          fileUploadService.getUpload(upload.id)
            .catch(err => {
              console.error(`Error fetching upload ${upload.id}:`, err);
              return null; // Return null on error to filter out later
            })
        );
        const updatedUploads = await Promise.all(updatePromises);
        const validUpdatedUploads = updatedUploads.filter(upload => upload !== null) as IFileUpload[];
        if (validUpdatedUploads.length > 0) {
          // Create a map of updated uploads by ID for quick lookup
          const updatedMap = new Map(validUpdatedUploads.map(upload => [upload.id, upload]));
          // Update allFileUploads by replacing old entries with new ones in the same position
          setAllFileUploads(prevUploads =>
            prevUploads.map(upload => {
              const updated = updatedMap.get(upload.id);
              return updated || upload; // Use updated version if available, otherwise keep original
            })
          );
        }
      } catch (err) {
        console.error('Error updating in-progress uploads:', err);
      }
    };
    // Set up interval to poll every 5 seconds
    const intervalId = setInterval(updateInProgressUploads, 15000);
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [allFileUploads, fileUploadService]);


  const getAllFileUploads = async () => {
    if (!reportPeriodContext.lastSelectedReportId) return;
    setIsDataLoading(true);
    try {
      const response = await fileUploadService.getAllUploadsWithPagination(
        selectedImportType,
        inputPage,
        perPage,
        orderBy,
        order,
        `&reportPeriodId=${reportPeriodContext.lastSelectedReportId}`
      );
      setAllFileUploads(response.rows);
      setTotalRecords(response.count);
    } catch (err) {
      console.log(err);
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
    } finally {
      setIsDataLoading(false);
    }
  };

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'fileName', numeric: false, label: 'File Name', sortable: true, isHeading: true },
    { id: 'uploadDate', numeric: false, label: 'Upload Date', sortable: true, isHeading: true },
    { id: 'dataType', numeric: false, label: 'Data Type', sortable: true, isHeading: true },
    { id: 'status', numeric: false, label: 'Status', sortable: true, isHeading: true },
    { id: 'records', numeric: true, label: 'Records', sortable: true, isHeading: true },
  ];
  const rows = !allFileUploads ? [] : allFileUploads.map(upload => {
    let statusChip = <Chip color='error' label='Failed' variant='outlined' sx={{ width: '130px' }} />
    if (upload.sourceType === 'BILL_DOCUMENTS' && upload.lineImportStatus === 'UNKNOWN') {
      // A bill import sits on 'UNKNOWN' deliberately — that keeps it off the CSV worker's PENDING
      // queue while Textract reads the documents. Without this branch it falls all the way through
      // to the `!importedContents` case and reads as 'Failed' for the whole of extraction, which is
      // exactly what a user sees while it is working normally.
      statusChip = <Chip color='info' label='Reading bills' variant='outlined' sx={{ width: '130px', animation: `${flashAnimation} 3s ease-in-out infinite` }} />
    } else if (upload.lineImportStatus === 'PENDING') {
      statusChip = <Chip color='info' label='Queued' variant='outlined' sx={{ width: '130px' }} />
    } else if (upload.lineImportStatus === 'IN_PROGRESS') {
      statusChip = <Chip color='info' label='Uploading' variant='outlined' sx={{ width: '130px', animation: `${flashAnimation} 3s ease-in-out infinite` }} />
    } else if (upload.lineImportStatus === 'FAILED') {
      statusChip = <Chip color='error' label='Failed' variant='outlined' sx={{ width: '130px' }} />
    } else if (upload.lineCommitStatus === 'PENDING' || upload.lineCommitStatus === 'IN_PROGRESS') {
      statusChip = <Chip color='info' label='Importing' variant='outlined' sx={{ width: '130px', animation: `${flashAnimation} 3s ease-in-out infinite` }} />
    } else if (upload.isCustomSpreadSheet && !upload.mapped && !upload.actionRequired) {
      statusChip = <Chip color='warning' label='Mapping Required' variant='outlined' sx={{ width: '130px' }} />
    } else if (upload.actionRequired) {
      statusChip = <Chip color='warning' label='Action Required' variant='outlined' sx={{ width: '130px' }} />
    } else if (!upload.importedContents) {
      statusChip = <Chip color='error' label='Failed' variant='outlined' sx={{ width: '130px' }} />
    } else {
      statusChip = <Chip color='success' label='Completed' variant='outlined' sx={{ width: '130px' }} />
    }

    const isCompleted = !(upload.isCustomSpreadSheet && !upload.mapped && !upload.actionRequired) &&
      !upload.actionRequired &&
      !!upload.importedContents;

    return {
      id: upload.id,
      fileName: upload.filename,
      uploadDate: moment(upload.uploadDate).format('DD/MM/YYYY HH:mm'),
      dataType: (upload.details && upload.details.startsWith('emissions')) ? 'Emissions Data' :
        upload.details === 'spend-based' ? 'Emissions Data' :
          upload.details === 'locations' ? screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' }) + ' Data' :
            upload.details === 'vehicles' ? screenLabelsContext.getLabel('vehicle', { plural: true, capitalize: 'first' }) + ' Data' :
              upload.details === 'products' ? screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' }) + ' Data' :
                upload.details === 'projects' ? screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' }) + ' Data' :
                  upload.details === 'suppliers' ? screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' }) + ' Data' :
                    upload.details,
      status: statusChip,
      // A bill upload's row count differs from its bill count (a credit note has no rows, a combined
      // bill has two), so say both rather than leave "51 bills" beside "44" unexplained.
      records: upload.sourceType === 'BILL_DOCUMENTS' && typeof upload.billDocumentCount === 'number' && upload.lineImportStatus !== 'UNKNOWN'
        ? `${(upload.numberOfRecords ?? 0).toLocaleString()} ${upload.numberOfRecords === 1 ? 'row' : 'rows'} from ${upload.billDocumentCount.toLocaleString()} ${upload.billDocumentCount === 1 ? 'bill' : 'bills'}`
        : upload.lineImportStatus === 'PENDING' ? `0 / ${upload.recordCount}` : upload.lineImportStatus === 'IN_PROGRESS' ? `${upload.numberOfRecords} / ${upload.recordCount}` : upload.isCustomSpreadSheet && !upload.mapped && !upload.actionRequired ? null : upload.numberOfRecords,
      //routeType, isCompleted and isMapped to aid handleSelected
      routeType: upload.details?.startsWith('emissions') ? 'emissions' : upload.details,
      isCompleted: isCompleted,
      isMapped: upload.mapped,
    }
  });

  //Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc') => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const handleImportTypeChange = (event: any) => {
    setSelectedImportType(event.target.value);
  };


  const handleSelected = async (ids: string[]) => {
    setSelected(ids);
    if (ids && ids.length === 1) {
      const row = rows.find(r => String(r.id) === String(ids[0]));
      if (row && row.routeType) {
        const upload = allFileUploads.find(u => String(u.id) === String(row.id));

        // Only check progress for uploads that are actually queued or in progress
        if (upload && (upload.lineImportStatus === 'PENDING' || upload.lineImportStatus === 'IN_PROGRESS')) {
          try {
            const progressData = await fileUploadService.checkFileUploadProgress(row.id);
            const totalRecords = progressData.recordCount ?? 0;
            const currentRecords = progressData.numberOfRecords ?? 0;
            // If upload is not complete, navigate to progress page
            if (totalRecords > 0 && currentRecords < totalRecords) {
              navigate(`/settings/data-upload/import-data/${row.id}`);
              return;
            }

          } catch (err) {
            console.error('Error checking progress:', err);
            // Continue with normal navigation if check fails
          }
        }

        console.log(upload)
        if (upload && (upload.lineCommitStatus === 'PENDING' || upload.lineCommitStatus === 'IN_PROGRESS')) {
          // returnTo=detail so the animation returns to the import-history detail page (Imported
          // tab) on completion — not the wizard's Review step (the default fallback).
          navigate(`/settings/data-upload/commit-progress/${row.id}?returnTo=detail&id=${row.routeType}&uploadId=${row.id}`);
          return;
        }

        // Navigate to the original route if upload is complete or check failed
        const url = !row.isMapped ?
          `/settings/data-upload/import-history/mapping/${row.routeType}/${row.id}`
          : row.isCompleted
            ? `/settings/data-upload/import-history/${row.routeType}/${row.id}?tabId=1`
            : `/settings/data-upload/import-history/${row.routeType}/${row.id}`;
        navigate(url);
      }
    }
  };

  return (
    <PageOuterContainer>
      {isMobileMode && <Box sx={{ pb: 4 }}>
        <SettingsMobileNavbar />
      </Box>}
      <PageHeading
        title='Import History'
        hasTabs={false}
      />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xl={3} lg={3} md={3} sm={12} xs={12}>
          <FormControl fullWidth variant='outlined' size='small'>
            <InputLabel htmlFor='importType'>Import Type</InputLabel>
            <Select
              id='importType'
              name='importType'
              label='Import Type'
              value={selectedImportType}
              onChange={handleImportTypeChange} >
              <MenuItem value='all'>All</MenuItem>
              <MenuItem value='emissions'>Emissions</MenuItem>
              <MenuItem value='locations'>{screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' })}</MenuItem>
              <MenuItem value='vehicles'>{screenLabelsContext.getLabel('vehicle', { plural: true, capitalize: 'first' })}</MenuItem>
              <MenuItem value='suppliers'>{screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' })}</MenuItem>
              <MenuItem value='products'>{screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' })}</MenuItem>
              <MenuItem value='projects'>{screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' })}</MenuItem>
              <MenuItem value='suppliers'>{screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' })}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <LoadingPage pageReady={!isDataLoading}>
        <TableDisplay
          onOrderChange={handleOrderChange}
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          handleSelected={handleSelected}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </LoadingPage>
    </PageOuterContainer>
  )
}
